/**
 * The loaded dictionary: every valid word, its one-letter neighbours and its lemma group,
 * held in typed arrays. Reads the compact binary file written by
 * wordgame/services/client_dictionary.py (magic "WLG1") and, for dictionaries not yet
 * regenerated, the legacy JSON object {word: {s: [...], d: [...], l: lemma}} or {word: true}.
 *
 * Croatian: 475k words in ~30 MB of memory instead of a 49 MB JSON object graph.
 */
import { lowerWord } from './letters';

const MAGIC = [0x57, 0x4c, 0x47, 0x31]; // "WLG1"

export class WordGraph {
    /**
     * @param {string[]} words sorted by UTF-16 code units (binary-searchable)
     * @param {Int32Array} offsets neighbours of word i are neighbors[offsets[i] .. offsets[i + 1])
     * @param {Int32Array} neighbors word indices
     * @param {Int32Array|null} lemmaIds lemma group per word, null when the language has no lemma data
     */
    constructor(words, offsets, neighbors, lemmaIds) {
        this.words = words;
        this.offsets = offsets;
        this.neighbors = neighbors;
        this.lemmaIds = lemmaIds;
        this.size = words.length;
        this.hasNeighborData = offsets[words.length] > 0;
    }

    indexOf(word) {
        const target = lowerWord(word);
        let lo = 0;
        let hi = this.words.length - 1;
        while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            const w = this.words[mid];
            if (w === target) return mid;
            if (w < target) lo = mid + 1;
            else hi = mid - 1;
        }
        return -1;
    }

    has(word) {
        return this.indexOf(word) >= 0;
    }

    neighborsOf(i) {
        return this.neighbors.subarray(this.offsets[i], this.offsets[i + 1]);
    }

    /** Same number = same lemma. Words without lemma data are their own group. */
    lemmaIdOf(i) {
        return this.lemmaIds ? this.lemmaIds[i] : -1 - i;
    }

    /** Comparable lemma key for any word, including ones not in the dictionary. */
    lemmaKey(word) {
        const i = this.indexOf(word);
        return i < 0 ? `?${lowerWord(word)}` : this.lemmaIdOf(i);
    }

    /** Decompressed dictionary bytes (binary or legacy JSON) -> WordGraph. */
    static decode(bytes) {
        if (bytes[0] === 0x7b) { // '{': legacy JSON dictionary
            return WordGraph.fromObject(JSON.parse(new TextDecoder().decode(bytes)));
        }
        for (let k = 0; k < MAGIC.length; k++) {
            if (bytes[k] !== MAGIC[k]) throw new Error('Unknown dictionary format');
        }
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const decoder = new TextDecoder();
        let pos = 4;
        const headerLength = view.getUint32(pos, true);
        pos += 4;
        const header = JSON.parse(decoder.decode(bytes.subarray(pos, pos + headerLength)));
        pos += headerLength;
        if (header.format !== 1 && header.format !== 2) throw new Error(`Unsupported dictionary format ${header.format}`);
        const wordsLength = view.getUint32(pos, true);
        pos += 4;
        const blockEnd = pos + wordsLength;

        const varint = () => {
            let value = 0;
            let shift = 0;
            let byte;
            do {
                byte = bytes[pos++];
                value |= (byte & 0x7f) << shift;
                shift += 7;
            } while (byte & 0x80);
            return value;
        };

        let joined = bytes.subarray(pos, blockEnd);
        if (header.format === 2) {
            // Front-coded: each word repeats `shared` bytes of the previous one, then adds its own suffix.
            joined = new Uint8Array(header.wordBytes);
            let out = 0;
            let previous = 0;
            for (let k = 0; k < header.words; k++) {
                const shared = varint();
                const length = varint();
                if (k > 0) joined[out++] = 0x0a;
                joined.copyWithin(out, previous, previous + shared);
                joined.set(bytes.subarray(pos, pos + length), out + shared);
                previous = out;
                out += shared + length;
                pos += length;
            }
        }
        const words = joined.length ? decoder.decode(joined).split('\n') : [];
        pos = blockEnd;
        const n = words.length;

        let lemmaIds = null;
        if (header.lemmas) {
            lemmaIds = new Int32Array(n);
            let previous = 0;
            for (let i = 0; i < n; i++) {
                const z = varint();
                previous += (z >>> 1) ^ -(z & 1);
                lemmaIds[i] = previous;
            }
        }

        // Each undirected edge is stored once (i < j); rebuild both directions.
        const edgeCount = header.edges;
        const from = new Int32Array(edgeCount);
        const to = new Int32Array(edgeCount);
        const degree = new Int32Array(n + 1);
        let e = 0;
        for (let i = 0; i < n; i++) {
            const count = varint();
            let j = i;
            for (let k = 0; k < count; k++) {
                j += varint();
                from[e] = i;
                to[e] = j;
                degree[i]++;
                degree[j]++;
                e++;
            }
        }
        const offsets = new Int32Array(n + 1);
        for (let i = 0; i < n; i++) offsets[i + 1] = offsets[i] + degree[i];
        const neighbors = new Int32Array(offsets[n]);
        const cursor = offsets.slice(0, n);
        for (let k = 0; k < e; k++) {
            neighbors[cursor[from[k]]++] = to[k];
            neighbors[cursor[to[k]]++] = from[k];
        }
        return new WordGraph(words, offsets, neighbors, lemmaIds);
    }

    /** Legacy JSON object (or a test fixture) -> WordGraph, keeping each word's neighbour order. */
    static fromObject(obj) {
        const words = Object.keys(obj || {}).sort();
        const index = new Map(words.map((w, i) => [w, i]));
        const offsets = new Int32Array(words.length + 1);
        const lists = words.map((w) => {
            const entry = obj[w];
            if (!entry || typeof entry !== 'object') return [];
            return [...(entry.s || []), ...(entry.d || [])].filter(n => index.has(n)).map(n => index.get(n));
        });
        lists.forEach((list, i) => { offsets[i + 1] = offsets[i] + list.length; });
        const neighbors = new Int32Array(offsets[words.length]);
        lists.forEach((list, i) => neighbors.set(list, offsets[i]));

        let lemmaIds = null;
        if (words.some(w => obj[w] && obj[w].l)) {
            const groups = new Map();
            lemmaIds = new Int32Array(words.length);
            words.forEach((w, i) => {
                const lemma = (obj[w] && obj[w].l) || w;
                if (!groups.has(lemma)) groups.set(lemma, groups.size);
                lemmaIds[i] = groups.get(lemma);
            });
        }
        return new WordGraph(words, offsets, neighbors, lemmaIds);
    }
}

const converted = new WeakMap();

/** A WordGraph for either a WordGraph or a plain {word: {s, d, l}} object; null for nothing. */
export function asGraph(dictionary) {
    if (!dictionary) return null;
    if (dictionary instanceof WordGraph) return dictionary;
    if (!converted.has(dictionary)) converted.set(dictionary, WordGraph.fromObject(dictionary));
    return converted.get(dictionary);
}
